import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';

/**
 * Draggable vertical divider for split-screen mode.
 * Props:
 *   splitPosition          {number}   - Current split position as 0–1 fraction
 *   onSplitPositionChange  {Function} - Called with the new fraction when dragged
 *   containerRef           {React.RefObject} - Ref to the outer container for width calculation
 */
export function SplitScreenDivider({ splitPosition, onSplitPositionChange, containerRef }) {
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = useCallback((event) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((event) => {
    if (!isDragging) return;
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return;
    const newPosition = (event.clientX - containerRect.left) / containerRect.width;
    onSplitPositionChange(Math.max(0.1, Math.min(0.9, newPosition)));
  }, [isDragging, containerRef, onSplitPositionChange]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'ew-resize';
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <Box
      sx={{
        position: 'absolute',
        left: `${splitPosition * 100}%`,
        top: 0,
        bottom: 0,
        width: '4px',
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        cursor: 'ew-resize',
        zIndex: 1050,
        transform: 'translateX(-2px)',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          width: '6px',
          transform: 'translateX(-3px)',
        }
      }}
      onMouseDown={handleMouseDown}
    />
  );
}

export default SplitScreenDivider;
